import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Trophy, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/memorial", label: "Memorial" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const isHome = loc.pathname === "/";
  const blueBar = !isHome; // solid blue banner on every non-home page
  const lightText = blueBar || !scrolled; // white text when over blue

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        blueBar ? "sky-gradient shadow-md" : scrolled ? "glass shadow-sm border-b border-white/40" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-white/20 text-white shadow-md group-hover:-translate-y-0.5 transition-transform">
            <Trophy size={18} />
          </span>
          <span className={`font-display text-lg leading-none ${lightText ? "text-white drop-shadow" : "text-slate-900"}`}>
            Darren Power<span className="block text-[10px] tracking-[0.25em] font-sans uppercase opacity-70">Memorial Classic</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === l.to
                  ? "bg-white/25 text-white"
                  : lightText ? "text-white/90 hover:bg-white/15" : "text-slate-700 hover:bg-sky-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-slate-900 text-white hover:-translate-y-0.5 transition-transform">
              <LayoutDashboard size={15} /> Dashboard
            </Link>
          )}
          {user ? (
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium ${lightText ? "text-white/90 hover:bg-white/15" : "text-slate-700 hover:bg-slate-100"}`}>
              <LogOut size={15} /> Sign out
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login" className={`px-3.5 py-2 rounded-full text-sm font-medium ${lightText ? "text-white/90 hover:bg-white/15" : "text-slate-700 hover:bg-sky-50"}`}>
              Sign in
            </Link>
          )}
          <Link to="/register" data-testid="nav-register-cta" className="px-4 py-2 rounded-full text-sm font-semibold bg-green-600 text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all">
            Enter Now
          </Link>
        </div>

        <button data-testid="mobile-menu-btn" className={`lg:hidden ${lightText ? "text-white" : "text-slate-900"}`} onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-white/40 px-5 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block px-3 py-2.5 rounded-lg text-slate-800 font-medium hover:bg-sky-50">{l.label}</Link>
          ))}
          {user?.role === "admin" && <Link to="/admin" className="block px-3 py-2.5 rounded-lg text-slate-800 font-medium hover:bg-sky-50">Dashboard</Link>}
          <div className="pt-2 flex gap-2">
            {user ? (
              <button onClick={logout} className="flex-1 px-3 py-2.5 rounded-lg bg-slate-100 font-medium">Sign out</button>
            ) : (
              <Link to="/login" className="flex-1 text-center px-3 py-2.5 rounded-lg bg-slate-100 font-medium">Sign in</Link>
            )}
            <Link to="/register" className="flex-1 text-center px-3 py-2.5 rounded-lg bg-green-600 text-white font-semibold">Enter Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}
